import {
    asCode,
    ANNEX_III_CATEGORY_LABELS,
    FRIA_DEPLOYER_TYPE_LABELS,
    labelForCode,
    labelsForCodes,
    mapAiActClassificationDetails,
    mapDataCategoriesDetails,
    mapTransferHostingDetails,
    TRANSPARENCY_TRIGGER_LABELS
} from "./summaryDetails";

const ASSESSMENT_STATUS_LABELS = {
    not_started: "Not Started",
    in_progress: "In Progress",
    completed: "Completed"
};

const ASSESSMENT_STATUS_COPY = {
    not_started: "No assessment yet.",
    in_progress: "Assessment started.",
    completed: "Assessment finished and no open issues detected."
};

const PRIVACY_RISK_LABELS = {
    not_assessed: "Not Assessed",
    low: "Low Risk",
    high: "High Risk"
};

const PRE_SCREEN_LABELS = {
    not_assessed: "Not Assessed",
    significant_risk_indicated: "Significant Risk Indicated",
    not_indicated: "No Significant Risk Indicated"
};

const YES_NO_LABELS = {
    not_assessed: "Not Assessed",
    yes: "Yes",
    no: "No"
};

const ASSESSMENT_STATUS_TONES = {
    not_started: "neutral",
    in_progress: "warning",
    completed: "success"
};

const FRIA_REQUIREMENT = {
    required: {
        label: "Required",
        tone: "rose",
        valueTone: "danger",
        description:
            "A FRIA is required for this AI system based on the current analysis."
    },
    not_required: {
        label: "Not Required",
        tone: "success",
        valueTone: "success",
        description:
            "No FRIA is required for this AI system based on the current analysis."
    },
    not_resolved: {
        label: "Not Resolved",
        tone: "neutral",
        valueTone: "",
        description: "The FRIA requirement has not been resolved yet."
    }
};

const FRIA_TRIGGER_LABELS = {
    full: "Full High Risk",
    full_high_risk: "Full High Risk",
    annex_iii: "Annex III"
};

const FRIA_TRIGGER_COPY = {
    annex_iii: "Annex III – High-risk use case.",
    full: "Full high-risk AI system.",
    full_high_risk: "Full high-risk AI system."
};

/**
 * Open FRIA findings are not part of the published FRIA summary contract yet,
 * so the card stays out of the strip until the backend sends the count.
 * Returns null while the field is absent — never 0, which would read as
 * "no open gaps" when the truth is unknown.
 */
const getOpenGapsCount = (summary) => {
    const source = summary.openGaps ?? summary.open_gaps;

    if (source == null) {
        return null;
    }

    if (typeof source === "number") {
        return source;
    }

    return source.count ?? null;
};

const joinLabels = (items = []) => {
    return items
        .map((item) => {
            if (typeof item === "string") {
                return item;
            }

            return item?.label;
        })
        .filter(Boolean)
        .join(", ");
};

export const mapAssessmentStatus = (assessmentStatus) => {
    if (!assessmentStatus?.value) {
        return null;
    }

    return {
        label: ASSESSMENT_STATUS_LABELS[assessmentStatus.value] || assessmentStatus.value,
        percent: assessmentStatus.completionPercent,
        description: ASSESSMENT_STATUS_COPY[assessmentStatus.value]
    };
};

const makeAssessmentStatusCard = (assessmentStatus) => {
    const status = mapAssessmentStatus(assessmentStatus);
    const statusValue = assessmentStatus?.value;

    if (!status) {
        return null;
    }

    return {
        key: "assessment-status",
        icon: "ri-file-list-3-line",
        tone: ASSESSMENT_STATUS_TONES[statusValue],
        title: "Assessment Status",
        info: "Shows the current completion state of this assessment.",
        badge: status.label,
        badgeTone: ASSESSMENT_STATUS_TONES[statusValue],
        value: status.label,
        valueTone: ASSESSMENT_STATUS_TONES[statusValue],
        description: status.description
    };
};

export const mapProcessingActivitySummary = (summary) => {
    if (!summary) {
        return {
            status: null,
            cards: []
        };
    }

    const privacyRisk = summary.privacyRisk || {};
    const riskLevel = privacyRisk.level;
    const driversCopy = joinLabels(privacyRisk.drivers);
    const dataCategories = summary.dataCategories || {};
    const transferHosting = summary.transferHostingIssues || {};
    const categoryCount = dataCategories.count || 0;
    const issueCount = transferHosting.count || 0;

    return {
        status: null,
        cards: [
            // Assessment Status is shown on the sub-tab card, not here.
            // makeAssessmentStatusCard(summary.assessmentStatus),
            {
                key: "privacy-risk",
                icon: "ri-shield-flash-line",
                tone: riskLevel === "high" ? "rose" : "blue",
                title: "Privacy Risk",
                info: "Shows the privacy risk level derived by the assessment summary.",
                badge: PRIVACY_RISK_LABELS[riskLevel] || riskLevel,
                badgeTone: riskLevel === "high" ? "rose" : "",
                value: PRIVACY_RISK_LABELS[riskLevel] || riskLevel,
                valueTone: riskLevel === "high" ? "danger" : riskLevel === "low" ? "success" : "",
                description: driversCopy || privacyRisk.reason || "",
                meta: privacyRisk.dpiaRequired ? "DPIA required" : null
            },
            {
                key: "data-categories",
                icon: "ri-book-2-line",
                tone: "blue",
                title: "Data Categories",
                info: "Shows the number of personal-data categories identified.",
                value: categoryCount,
                valueLabel: categoryCount > 0 ? "Identified" : "None selected",
                description: joinLabels(dataCategories.categories),
                actionLabel: "View details",
                details: mapDataCategoriesDetails(dataCategories)
            },
            {
                key: "transfer-hosting",
                icon: "ri-global-line",
                tone: "violet",
                title: "Transfer and hosting issues",
                info: "Shows open transfer and infrastructure-hosting inconsistencies.",
                value: issueCount,
                valueLabel:
                    issueCount > 0
                        ? "Require attention"
                        : "No issues detected",
                valueTone: issueCount > 0 ? "warning" : "",
                description: joinLabels(
                    (transferHosting.issues || []).map((issue) => ({
                        label: issue.title
                    }))
                ),
                actionLabel: "View details",
                details: mapTransferHostingDetails(transferHosting)
            }
        ].filter(Boolean)
    };
};

export const mapRiskAssessmentSummary = (summary) => {
    if (!summary) {
        return {
            status: null,
            cards: []
        };
    }

    const statusValue = summary.assessmentStatus?.value;
    const preScreen = summary.preScreenOutcome || {};
    const indicators = summary.riskIndicators || {};
    const blacklist = summary.blacklistCheck || {};
    const indicatorCount = indicators.count || 0;
    const assessed = statusValue === "completed";
    let indicatorLabel;

    if (indicatorCount > 0) {
        indicatorLabel = "Identified";
    } else if (assessed) {
        indicatorLabel = "None identified";
    }

    return {
        status: null,
        cards: [
            // makeAssessmentStatusCard(summary.assessmentStatus),
            {
                key: "pre-screen-outcome",
                icon: "ri-alert-line",
                tone: preScreen.value === "significant_risk_indicated" ? "rose" : "blue",
                title: "Pre-screen Outcome",
                info: "Summarizes whether the pre-screen identified significant-risk indicators.",
                badge:
                    PRE_SCREEN_LABELS[preScreen.value] || preScreen.value,
                badgeTone:
                    preScreen.value === "significant_risk_indicated"
                        ? "rose"
                        : preScreen.value === "not_indicated"
                        ? "success"
                        : "",
                value:
                    PRE_SCREEN_LABELS[preScreen.value] || preScreen.value,
                valueTone:
                    preScreen.value === "significant_risk_indicated"
                        ? "danger"
                        : preScreen.value === "not_indicated"
                        ? "success"
                        : "",
                valueSize: "compact",
                description: joinLabels(preScreen.drivers) || preScreen.reason || ""
            },
            {
                key: "risk-indicators",
                icon: "ri-error-warning-line",
                tone: "blue",
                title: "Risk Indicators",
                info: "Shows how many of the screening criteria were answered Yes.",
                value: indicatorCount,
                valueLabel: indicatorLabel,
                description:
                    indicators.total != null
                        ? {
                            key: "{{number}} of {{total}} screening criteria answered “Yes”.",
                            values: {
                                number: indicatorCount,
                                total: indicators.total
                            }
                        }
                        : joinLabels(indicators.indicators)
            },
            {
                key: "blacklist-check",
                icon: "ri-shield-line",
                tone: blacklist.value === "no" ? "success" : "rose",
                title: "Blacklist Check",
                info: "Shows whether a DPO blacklist criterion was identified.",
                badge: YES_NO_LABELS[blacklist.value] || blacklist.value,
                badgeTone: blacklist.value === "no" ? "success" : blacklist.value === "yes" ? "rose" : "",
                value: YES_NO_LABELS[blacklist.value] || blacklist.value,
                valueTone: blacklist.value === "no" ? "success" : blacklist.value === "yes" ? "danger" : "",
                description:
                    blacklist.value === "yes"
                        ? "DPO blacklist criterion identified"
                        : blacklist.value === "no"
                        ? "No DPO blacklist criterion identified"
                        : ""
            }
        ].filter(Boolean)
    };
};

const AI_CLASSIFICATION_LABELS = {
    minimal_risk: {
        label: "Minimal Risk",
        tone: "success",
        valueTone: "success"
    },
    limited_risk: {
        label: "Limited Risk",
        tone: "warning",
        valueTone: "warning"
    },
    high_risk: {
        label: "High Risk",
        tone: "rose",
        valueTone: "danger"
    },
    prohibited: {
        label: "Prohibited",
        tone: "rose",
        valueTone: "danger"
    },
    review_required: {
        label: "Review Required",
        tone: "warning",
        valueTone: "warning"
    }
};

const formatAiActClassificationReason = (classification) => {
    const useCases = labelsForCodes(
        classification?.annex_iii_use_cases,
        ANNEX_III_CATEGORY_LABELS
    );

    if (useCases.length) {
        return { labels: useCases };
    }

    const reason = classification?.reasons?.[0];

    if (reason) {
        return { literal: reason };
    }

    return "";
};

const makeObligationsDescription = ({
    gapsCount,
    reviewCount,
    informationMissingCount
}) => {
    return {
        key: "{{review}} review, {{missing}} missing, {{gaps}} gaps.",
        values: {
            review: reviewCount,
            missing: informationMissingCount,
            gaps: gapsCount
        }
    };
};

export const mapEuAiActSummary = (summary) => {
    if (!summary) {
        return {
            status: null,
            cards: []
        };
    }

    const aiActAssessment =
        summary.aiActAssessment || summary.ai_act_assessment || {};
    const classification = aiActAssessment.classification || {};
    const classificationValue = asCode(
        aiActAssessment.status || classification.status
    );
    const classificationConfig = AI_CLASSIFICATION_LABELS[classificationValue] || {
        label: classificationValue || "Not Assessed",
        tone: "neutral",
        valueTone: ""
    };
    const classificationLabel = classificationConfig.label;
    const track = aiActAssessment.track;
    const transparency = classification.transparency || {};
    const transparencyApplicable = transparency.applicable === true;
    const transparencyTriggers = labelsForCodes(
        transparency.triggers,
        TRANSPARENCY_TRIGGER_LABELS
    );
    const transparencyCount = transparencyApplicable
        ? transparencyTriggers.length
        : 0;
    const obligations = summary.obligations || {};
    const gapsCount = obligations.gapsCount ?? 0;
    const reviewCount = obligations.reviewCount ?? 0;
    const informationMissingCount =
        obligations.informationMissingCount ?? 0;
    const attentionCount =
        gapsCount + reviewCount + informationMissingCount;
    const complianceState =
        attentionCount > 0
            ? "needs_attention"
            : summary.hasAssessment ||
              summary.assessmentStatus?.value === "completed"
            ? "compliant"
            : "not_assessed";
    const complianceLabel =
        complianceState === "compliant"
            ? "Compliant"
            : complianceState === "not_assessed"
            ? "Not Assessed"
            : "Needs attention";

    return {
        status: null,
        titleBadge: track
            ? String(track).charAt(0).toUpperCase() +
              String(track).slice(1)
            : null,
        cards: [
            // makeAssessmentStatusCard(summary.assessmentStatus),
            {
                key: "ai-act-classification",
                icon: "ri-shield-flash-line",
                tone: classificationConfig.tone,
                title: "AI Act Classification",
                info: "Shows the AI Act classification returned by the assessment.",
                badge: classificationLabel,
                badgeTone: classificationConfig.tone,
                value: classificationLabel,
                valueTone: classificationConfig.valueTone,
                valueSize: "compact",
                description: formatAiActClassificationReason(classification),
                actionLabel: "View summary",
                details: mapAiActClassificationDetails(aiActAssessment)
            },
            {
                key: "transparency",
                icon: "ri-file-list-2-line",
                tone: "blue",
                title: "Transparency",
                info: "Shows the transparency obligations applicable to this AI system.",
                value: transparencyCount,
                valueLabel: transparencyApplicable
                    ? "Applicable"
                    : "Not applicable",
                description:
                    transparencyApplicable
                        ? transparencyTriggers.length
                            ? { labels: transparencyTriggers }
                            : {
                                key:
                                    transparencyCount === 1
                                        ? "{{number}} transparency trigger identified."
                                        : "{{number}} transparency triggers identified.",
                                values: { number: transparencyCount }
                              }
                        : "No transparency obligations apply."
            },
            {
                key: "deployer-compliance",
                icon: "ri-building-line",
                tone:
                    complianceState === "compliant"
                        ? "success"
                        : complianceState === "not_assessed"
                        ? "neutral"
                        : "warning",
                title: track === "full"
                    ? "Provider Compliance"
                    : "Deployer Compliance",
                info: "Shows the current compliance state of the applicable obligations.",
                badge: complianceLabel,
                badgeTone:
                    complianceState === "compliant"
                        ? "success"
                        : complianceState === "not_assessed"
                        ? "neutral"
                        : "warning",
                value:
                    complianceState === "not_assessed"
                        ? "Not Assessed"
                        : attentionCount,
                valueLabel:
                    complianceState === "compliant"
                        ? "Compliant"
                        : complianceState === "needs_attention"
                        ? "Needs attention"
                        : null,
                valueTone:
                    complianceState === "compliant"
                        ? "success"
                        : complianceState === "needs_attention"
                        ? "warning"
                        : "",
                valueSize:
                    complianceState === "not_assessed" ? "compact" : "",
                description:
                    complianceState === "not_assessed"
                        ? "Complete the assessment to review obligations."
                        : makeObligationsDescription({
                            gapsCount,
                            reviewCount,
                            informationMissingCount
                        })
            }
        ].filter(Boolean)
    };
};

export const mapDpiaSummary = (summary) => {
    if (!summary) {
        return {
            status: null,
            cards: []
        };
    }

    const rightsRisks = summary.rightsRisks || {};
    const safeguards = summary.safeguards || {};
    const remaining = summary.significantRiskRemaining || {};

    return {
        status: mapAssessmentStatus(summary.assessmentStatus),
        cards: [
            {
                key: "rights-risks",
                icon: "ri-user-heart-line",
                tone: rightsRisks.value === "yes" ? "rose" : "blue",
                title: "Rights Risks Identified",
                value: YES_NO_LABELS[rightsRisks.value] || rightsRisks.value,
                description: rightsRisks.reason || ""
            },
            {
                key: "safeguards",
                icon: "ri-shield-check-line",
                tone: "blue",
                title: "Safeguards / TOM",
                value: `${safeguards.confirmedCount ?? 0} / ${safeguards.totalCount ?? 0}`,
                literalValue: true,
                valueLabel: "Answered Yes",
                description: ""
            },
            {
                key: "significant-risk-remaining",
                icon: "ri-alert-line",
                tone: remaining.value === "yes" ? "rose" : "violet",
                title: "Significant Risk Remaining",
                value: YES_NO_LABELS[remaining.value] || remaining.value,
                description: remaining.reason || ""
            }
        ]
    };
};

export const mapFriaSummary = (summary) => {
    if (!summary) {
        return {
            status: null,
            cards: []
        };
    }

    const requirementKey =
        summary.friaRequired === true
            ? "required"
            : summary.friaRequired === false
            ? "not_required"
            : "not_resolved";
    const requirement = FRIA_REQUIREMENT[requirementKey];
    const trigger = summary.friaTrigger || {};
    const deployerType = labelForCode(
        trigger.deployer_type ||
            summary.friaDeployerType ||
            summary.fria_deployer_type,
        FRIA_DEPLOYER_TYPE_LABELS
    );
    const triggerLabels = [
        ...labelsForCodes(trigger.use_cases, ANNEX_III_CATEGORY_LABELS),
        deployerType
    ].filter(Boolean);
    const openGaps = getOpenGapsCount(summary);

    return {
        status: null,
        cards: [
            // makeAssessmentStatusCard(summary.assessmentStatus),
            {
                key: "fria-requirement",
                icon: "ri-shield-line",
                tone: requirement.tone,
                title: "FRIA Requirement",
                info: "Shows whether a FRIA is currently required for this software.",
                badge: requirement.label,
                badgeTone: requirement.tone,
                value: requirement.label,
                valueTone: requirement.valueTone,
                valueSize: "compact",
                description: requirement.description
            },
            {
                key: "fria-trigger",
                icon: "ri-focus-3-line",
                tone: "blue",
                title: "Trigger / Use Case",
                info: "Shows the backend-provided trigger used to explain the FRIA requirement.",
                value:
                    triggerLabels[0] ||
                    FRIA_TRIGGER_LABELS[trigger.type] ||
                    "Not Applicable",
                valueSize: "compact",
                description:
                    FRIA_TRIGGER_COPY[trigger.type] ||
                    (trigger.reason ? { literal: trigger.reason } : "")
            },
            openGaps == null
                ? null
                : {
                    key: "fria-open-gaps",
                    icon:
                        openGaps > 0
                            ? "ri-error-warning-line"
                            : "ri-checkbox-circle-line",
                    tone: openGaps > 0 ? "warning" : "success",
                    title: "Open Gaps",
                    info: "Shows how many FRIA findings or actions are still open.",
                    value: openGaps,
                    valueLabel:
                        openGaps > 0
                            ? "Require attention"
                            : "No issues detected",
                    valueTone: openGaps > 0 ? "warning" : "",
                    description:
                        "Open FRIA-related findings or unresolved actions."
                }
        ].filter(Boolean)
    };
};
